import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../../../hooks/useApi';
import { ParserMarketPanel } from './AdminParserMarket';
import { fmtDateTime } from '../../../utils/ruDates';

const TASK_TYPES = [
  { value: 'ozon.product_snapshot.v1', label: 'Ozon: цена карточки' },
  { value: 'ozon.search_position.v1', label: 'Ozon: позиции по запросу' },
  { value: 'ozon.shelf_snapshot.v1', label: 'Ozon: рекомендательные полки' },
  { value: 'ozon.listing_snapshot.v1', label: 'Ozon: парсинг по ссылке' },
];

const STATUS_LABELS = {
  pending: 'В очереди',
  leased: 'Выполняется',
  needs_user_action: 'Ждёт пользователя',
  completed: 'Готово',
  failed: 'Ошибка',
  cancelled: 'Отменено',
};

const QUALITY_LABELS = {
  valid: 'Данные приняты',
  invalid: 'Есть ошибки качества',
  warning: 'Есть предупреждения',
};

const styles = {
  page: { display: 'flex', flexDirection: 'column', gap: 16 },
  header: { display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-start' },
  title: { margin: 0, fontSize: 26, fontWeight: 700 },
  subtitle: { marginTop: 6, color: '#64748b', fontSize: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 },
  cardTitle: { color: '#64748b', fontSize: 12, textTransform: 'uppercase', letterSpacing: '.04em' },
  cardValue: { marginTop: 8, fontSize: 28, fontWeight: 700 },
  panel: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 },
  panelHeader: { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 12 },
  tableWrap: { overflow: 'auto', maxHeight: 420, border: '1px solid #edf2f7', borderRadius: 8 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', whiteSpace: 'nowrap' },
  td: { padding: '9px 8px', borderBottom: '1px solid #f1f5f9', verticalAlign: 'top' },
  input: { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8 },
  row: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 },
  button: { border: '1px solid #2563eb', background: '#2563eb', color: '#fff', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' },
  ghostButton: { border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', borderRadius: 8, padding: '8px 12px', cursor: 'pointer' },
  muted: { color: '#64748b' },
};

function formatDate(value) {
  if (!value) return '—';
  return fmtDateTime(value);
}

function compactJson(value) {
  if (!value) return '—';
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function Badge({ value, tone = 'neutral' }) {
  const colors = {
    neutral: ['#f1f5f9', '#334155'],
    good: ['#dcfce7', '#166534'],
    bad: ['#fee2e2', '#991b1b'],
    warn: ['#fef3c7', '#92400e'],
    info: ['#dbeafe', '#1d4ed8'],
  }[tone];
  return (
    <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 999, fontSize: 12, background: colors[0], color: colors[1], whiteSpace: 'nowrap' }}>
      {value || '—'}
    </span>
  );
}

function taskStatusTone(status) {
  if (status === 'completed') return 'good';
  if (status === 'failed') return 'bad';
  if (status === 'needs_user_action') return 'warn';
  if (status === 'leased') return 'info';
  return 'neutral';
}

function qualityTone(status) {
  if (status === 'valid') return 'good';
  if (status === 'invalid') return 'bad';
  return 'warn';
}

function ParserHubOperations() {
  const { request, showStatus } = useApi();
  const [summary, setSummary] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    sourceService: 'manual_admin',
    taskType: 'ozon.search_position.v1',
    query: 'кофе в зернах 1 кг',
    listingUrl: '',
    skuList: '',
    maxItems: 108,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextSummary, nextTasks, nextSnapshots] = await Promise.all([
        request('/api/v1/parser/summary'),
        request('/api/v1/parser/tasks?limit=200'),
        request('/api/v1/parser/snapshots?quality_status=&limit=100'),
      ]);
      setSummary(nextSummary);
      setTasks(nextTasks);
      setSnapshots(nextSnapshots);
    } catch (error) {
      showStatus('error', `Ошибка загрузки хаба парсинга: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [request, showStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const counters = useMemo(() => {
    const taskCounts = summary?.tasks_by_status || {};
    const qualityCounts = summary?.snapshots_by_quality || {};
    return {
      pending: taskCounts.pending || 0,
      leased: taskCounts.leased || 0,
      needsUserAction: taskCounts.needs_user_action || 0,
      completed: taskCounts.completed || 0,
      failed: taskCounts.failed || 0,
      valid: qualityCounts.valid || 0,
      invalid: qualityCounts.invalid || 0,
      rows: summary?.rows_count || 0,
    };
  }, [summary]);

  const loadRows = async (snapshot) => {
    setSelectedSnapshot(snapshot);
    setRows([]);
    try {
      const nextRows = await request(`/api/v1/parser/snapshots/${snapshot.id}/rows?limit=500`);
      setRows(nextRows);
    } catch (error) {
      showStatus('error', `Ошибка загрузки строк: ${error.message}`);
    }
  };

  const retryTask = async (taskId) => {
    try {
      await request(`/api/v1/parser/tasks/${taskId}/retry`, { method: 'POST' });
      showStatus('success', 'Повторная задача создана');
      await load();
    } catch (error) {
      showStatus('error', `Не удалось создать повтор: ${error.message}`);
    }
  };

  const createBatch = async () => {
    const skus = form.skuList
      .split(/\s|,|;/)
      .map((value) => value.trim())
      .filter(Boolean);
    const query = form.query.trim();

    if (form.taskType === 'ozon.product_snapshot.v1' && !skus.length) {
      showStatus('error', 'Для цены карточки укажите хотя бы один SKU');
      return;
    }
    if (form.taskType === 'ozon.search_position.v1' && (!query || !skus.length)) {
      showStatus('error', 'Для позиций по запросу укажите ключевой запрос и целевые SKU');
      return;
    }
    if (form.taskType === 'ozon.shelf_snapshot.v1' && !query && !skus.length) {
      showStatus('error', 'Для полок укажите ключевой запрос или список SKU');
      return;
    }
    if (form.taskType === 'ozon.listing_snapshot.v1' && !form.listingUrl.trim()) {
      showStatus('error', 'Для парсинга по ссылке укажите ссылку Ozon');
      return;
    }

    let taskInputs = [];
    if (form.taskType === 'ozon.product_snapshot.v1') {
      taskInputs = skus.map((sku) => ({ sku }));
    } else if (form.taskType === 'ozon.search_position.v1') {
      taskInputs = [{
        query,
        target_skus: skus,
        max_items: Number(form.maxItems) || 108,
      }];
    } else if (form.taskType === 'ozon.shelf_snapshot.v1') {
      taskInputs = [{
        source_mode: skus.length ? 'explicit_skus' : 'top_search',
        source_skus: skus,
        query,
        top_n: 12,
      }];
    } else {
      const maxItems = Number(form.maxItems);
      taskInputs = [{
        url: form.listingUrl.trim(),
        ...(maxItems > 0 ? { max_items: maxItems } : {}),
      }];
    }

    setCreating(true);
    try {
      const created = await request('/api/v1/parser/task-batches', {
        method: 'POST',
        body: JSON.stringify({
          source_service: form.sourceService.trim() || 'manual_admin',
          dedupe_policy: 'skip_open_same_input',
          tasks: taskInputs.map((input) => ({
            task_type: form.taskType,
            input,
          })),
        }),
      });
      showStatus('success', `Создано задач: ${created.created_count}, пропущено дублей: ${created.skipped_count}`);
      await load();
    } catch (error) {
      showStatus('error', `Ошибка создания задач: ${error.message}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div style={styles.subtitle}>Очередь задач, снимки результатов, контроль качества и повтор ошибочных заданий.</div>
        <button type="button" style={styles.ghostButton} onClick={load} disabled={loading}>
          {loading ? 'Обновление...' : 'Обновить'}
        </button>
      </div>

      <div style={styles.grid}>
        <div style={styles.card}><div style={styles.cardTitle}>В очереди</div><div style={styles.cardValue}>{counters.pending}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Выполняется</div><div style={styles.cardValue}>{counters.leased}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Ждёт пользователя</div><div style={styles.cardValue}>{counters.needsUserAction}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Готово</div><div style={styles.cardValue}>{counters.completed}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Ошибки</div><div style={styles.cardValue}>{counters.failed}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Валидные снимки</div><div style={styles.cardValue}>{counters.valid}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Проблемные снимки</div><div style={styles.cardValue}>{counters.invalid}</div></div>
        <div style={styles.card}><div style={styles.cardTitle}>Строк данных</div><div style={styles.cardValue}>{counters.rows}</div></div>
      </div>

      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <div>
            <h2 style={{ margin: 0 }}>Создать тестовую пачку</h2>
            <div style={styles.subtitle}>Для ручной проверки агента без отдельного инструмента-источника.</div>
          </div>
          <button type="button" style={styles.button} onClick={createBatch} disabled={creating}>
            {creating ? 'Создаю...' : 'Создать задачи'}
          </button>
        </div>
        <div style={styles.row}>
          <label>
            <div>Источник</div>
            <input style={styles.input} value={form.sourceService} onChange={(e) => setForm({ ...form, sourceService: e.target.value })} />
          </label>
          <label>
            <div>Тип задачи</div>
            <select style={styles.input} value={form.taskType} onChange={(e) => setForm({ ...form, taskType: e.target.value })}>
              {TASK_TYPES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <div>Ключевой запрос</div>
            <input style={styles.input} value={form.query} onChange={(e) => setForm({ ...form, query: e.target.value })} />
          </label>
          <label>
            <div>Ссылка Ozon</div>
            <input style={styles.input} placeholder="https://www.ozon.ru/brand/..." value={form.listingUrl} onChange={(e) => setForm({ ...form, listingUrl: e.target.value })} />
          </label>
          <label>
            <div>Максимум товаров</div>
            <input type="number" min="1" max="10000" style={styles.input} value={form.maxItems} onChange={(e) => setForm({ ...form, maxItems: e.target.value })} />
          </label>
        </div>
        <label style={{ display: 'block', marginTop: 12 }}>
          <div>SKU / артикулы, через пробел или с новой строки</div>
          <textarea rows={4} style={styles.input} value={form.skuList} onChange={(e) => setForm({ ...form, skuList: e.target.value })} />
        </label>
      </div>

      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <h2 style={{ margin: 0 }}>Очередь задач</h2>
          <span style={styles.muted}>Последние 200 задач</span>
        </div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Статус</th>
                <th style={styles.th}>Тип</th>
                <th style={styles.th}>Входные данные</th>
                <th style={styles.th}>Попытка</th>
                <th style={styles.th}>Создано</th>
                <th style={styles.th}>Завершено</th>
                <th style={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((task) => (
                <tr key={task.id}>
                  <td style={styles.td}><Badge value={STATUS_LABELS[task.status] || task.status} tone={taskStatusTone(task.status)} /></td>
                  <td style={styles.td}>{task.kind}</td>
                  <td style={{ ...styles.td, maxWidth: 420, wordBreak: 'break-word' }}>{compactJson(task.payload)}</td>
                  <td style={styles.td}>{task.attempt} / {task.max_attempts}</td>
                  <td style={styles.td}>{formatDate(task.created_at)}</td>
                  <td style={styles.td}>{formatDate(task.finished_at)}</td>
                  <td style={styles.td}>
                    {(task.status === 'failed' || task.status === 'completed') && (
                      <button type="button" style={styles.ghostButton} onClick={() => retryTask(task.id)}>Повторить</button>
                    )}
                  </td>
                </tr>
              ))}
              {!tasks.length && <tr><td style={styles.td} colSpan={7}>Задач нет</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div style={styles.panel}>
        <div style={styles.panelHeader}>
          <h2 style={{ margin: 0 }}>Снимки результатов</h2>
          <span style={styles.muted}>Последние 100 снимков</span>
        </div>
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Качество</th>
                <th style={styles.th}>Тип</th>
                <th style={styles.th}>Источник</th>
                <th style={styles.th}>Строк</th>
                <th style={styles.th}>Город</th>
                <th style={styles.th}>Авторизация</th>
                <th style={styles.th}>МСК</th>
                <th style={styles.th}>Действия</th>
              </tr>
            </thead>
            <tbody>
              {snapshots.map((snapshot) => (
                <tr key={snapshot.id}>
                  <td style={styles.td}><Badge value={QUALITY_LABELS[snapshot.quality_status] || snapshot.quality_status} tone={qualityTone(snapshot.quality_status)} /></td>
                  <td style={styles.td}>{snapshot.task_type}</td>
                  <td style={styles.td}>{snapshot.source_service || '—'}</td>
                  <td style={styles.td}>{snapshot.summary?.rows_count ?? snapshot.summary?.items_scanned ?? '—'}</td>
                  <td style={styles.td}>{snapshot.summary?.ozon_city || snapshot.context?.ozon_city || '—'}</td>
                  <td style={styles.td}>{snapshot.summary?.ozon_authorized ?? snapshot.context?.ozon_authorized ? 'Да' : 'Нет'}</td>
                  <td style={styles.td}>{snapshot.snapshot_at_msk || formatDate(snapshot.snapshot_at_utc)}</td>
                  <td style={styles.td}><button type="button" style={styles.ghostButton} onClick={() => loadRows(snapshot)}>Строки</button></td>
                </tr>
              ))}
              {!snapshots.length && <tr><td style={styles.td} colSpan={8}>Снимков нет</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {selectedSnapshot && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <div>
              <h2 style={{ margin: 0 }}>Строки снимка</h2>
              <div style={styles.subtitle}>{selectedSnapshot.id}</div>
            </div>
            <Badge value={`${rows.length} строк`} tone="info" />
          </div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>№</th>
                  <th style={styles.th}>Позиция</th>
                  <th style={styles.th}>SKU</th>
                  <th style={styles.th}>Название</th>
                  <th style={styles.th}>Цена</th>
                  <th style={styles.th}>Город</th>
                  <th style={styles.th}>Авторизован</th>
                  <th style={styles.th}>Ссылка</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>{row.row_index}</td>
                    <td style={styles.td}>{row.position ?? '—'}</td>
                    <td style={styles.td}>{row.sku || '—'}</td>
                    <td style={{ ...styles.td, minWidth: 260 }}>{row.title || '—'}</td>
                    <td style={styles.td}>{row.price ? `${row.price} ${row.currency || ''}` : '—'}</td>
                    <td style={styles.td}>{row.city || '—'}</td>
                    <td style={styles.td}>{row.authorized ? 'Да' : 'Нет'}</td>
                    <td style={styles.td}>{row.url ? <a href={row.url} target="_blank" rel="noopener noreferrer">Открыть</a> : '—'}</td>
                  </tr>
                ))}
                {!rows.length && <tr><td style={styles.td} colSpan={8}>Строки не найдены</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

const HUB_TABS = [
  { key: 'overview', label: 'Задачи и результаты' },
  { key: 'executors', label: 'Исполнители' },
];

function ParserExecutors() {
  const { request } = useApi();
  const [tokens, setTokens] = useState([]);
  const [tokenError, setTokenError] = useState('');

  useEffect(() => {
    request('/api/v1/parser-agent/tokens')
      .then(setTokens)
      .catch((error) => setTokenError(error.message));
  }, [request]);

  const activeTokens = tokens.filter((token) => !token.revoked_at);
  const lastSeen = activeTokens
    .map((token) => token.last_used_at)
    .filter(Boolean)
    .sort()
    .at(-1);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={styles.grid}>
        <div style={styles.card}>
          <div style={styles.cardTitle}>Windows-агент</div>
          <div style={{ ...styles.cardValue, fontSize: 20 }}>
            {tokenError ? 'Ошибка проверки' : activeTokens.length ? 'Настроен' : 'Не настроен'}
          </div>
          <div style={styles.subtitle}>
            Активных токенов: {activeTokens.length}. Последняя связь: {formatDate(lastSeen)}
          </div>
        </div>
        <div style={styles.card}>
          <div style={styles.cardTitle}>parser.market</div>
          <div style={{ ...styles.cardValue, fontSize: 20 }}>Платный исполнитель</div>
          <div style={styles.subtitle}>Настройки подключения, баланс и журнал находятся ниже.</div>
        </div>
      </div>
      {tokenError && <div style={{ color: '#991b1b' }}>Не удалось проверить Windows-агент: {tokenError}</div>}
      <ParserMarketPanel embedded />
    </div>
  );
}

export default function AdminParserHub() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') === 'executors' ? 'executors' : 'overview';

  const selectTab = (nextTab) => {
    const next = new URLSearchParams(searchParams);
    if (nextTab === 'overview') next.delete('tab');
    else next.set('tab', nextTab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="admin-page">
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ marginBottom: 6 }}>Хаб парсинга</h1>
        <p style={{ color: '#64748b', margin: 0 }}>
          Общая очередь, результаты и способы выполнения задач парсинга.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {HUB_TABS.map((item) => (
          <button
            type="button"
            key={item.key}
            className={`btn btn--sm ${tab === item.key ? 'btn--primary' : 'btn--ghost'}`}
            onClick={() => selectTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {tab === 'overview' ? <ParserHubOperations /> : <ParserExecutors />}
    </div>
  );
}
